import React, { useState } from 'react';
import { ArrowLeft, Save, ChevronRight, Plus, X, Search, Building2, Network, FileText } from 'lucide-react';

const ProjectMilestoneForm = ({ onBack, projectData = null, mode = 'add', onSave }) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    projectCode: projectData?.projectCode || '',
    projectName: projectData?.projectName || '',
    spkId: projectData?.spkId || '',
    spkNumber: projectData?.spkNumber || '',
    customerName: projectData?.customerName || '',
    projectType: projectData?.projectType || 'TOWER',
    startDate: projectData?.startDate || '',
    endDate: projectData?.endDate || ''
  });

  const [selectedItems, setSelectedItems] = useState(
    projectData?.projectType === 'TOWER' ? (projectData?.towerSites || []) : (projectData?.segments || [])
  );

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showBrowseModal, setShowBrowseModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);

  // Mock data
  const availableSPKs = [
    { id: 1, spkNumber: 'SPK/TSEL/2025/001', customer: 'PT Telekomunikasi Selular', projectType: 'TOWER' },
    { id: 2, spkNumber: 'SPK/IOH/2025/002', customer: 'PT Indonesia Opera Helvetia', projectType: 'FO' },
    { id: 3, spkNumber: 'SPK/XLS/2025/003', customer: 'PT XL Smart Telecom', projectType: 'TOWER' },
    { id: 5, spkNumber: 'SPK/BAKTI/2025/005', customer: 'BAKTI', projectType: 'FO' }
  ];

  const availableTowerSites = [
    { id: 6, siteName: 'JKT-North-001', location: 'Kelapa Gading', category: 'RING', isAttached: false },
    { id: 7, siteName: 'JKT-North-002', location: 'Sunter', category: 'STAR', isAttached: false },
    { id: 8, siteName: 'JKT-West-001', location: 'Cengkareng', category: 'INSERT', isAttached: false }
  ];

  const availableSegments = [
    { id: 6, segmentName: 'Segment D', startPoint: 'Bandung', endPoint: 'Cirebon', length: 120, isAttached: false },
    { id: 7, segmentName: 'Segment E', startPoint: 'Semarang', endPoint: 'Solo', length: 95, isAttached: false }
  ];

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Handle SPK selection
    if (name === 'spkId') {
      const selectedSPK = availableSPKs.find(spk => spk.id === parseInt(value));
      if (selectedSPK) {
        setFormData(prev => ({ 
          ...prev, 
          spkId: value,
          spkNumber: selectedSPK.spkNumber,
          customerName: selectedSPK.customer,
          projectType: selectedSPK.projectType
        }));
        setSelectedItems([]); // Reset items when SPK changes
      }
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }

    // Reset items when project type changes
    if (name === 'projectType') {
      setSelectedItems([]);
    }
  };

  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.spkId) newErrors.spkId = 'SPK is required';
    if (!formData.projectCode.trim()) newErrors.projectCode = 'Project Code is required';
    if (!formData.projectName.trim()) newErrors.projectName = 'Project Name is required';
    if (!formData.startDate) newErrors.startDate = 'Start Date is required';
    if (!formData.endDate) newErrors.endDate = 'End Date is required';
    
    if (formData.startDate && formData.endDate && formData.startDate > formData.endDate) {
      newErrors.endDate = 'End Date must be after Start Date';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = () => {
    const newErrors = {};
    if (selectedItems.length === 0) {
      newErrors.items = `At least one ${formData.projectType === 'TOWER' ? 'tower site' : 'segment'} is required`;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 1 && validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleAddItem = (item) => {
    if (!selectedItems.find(i => i.id === item.id)) {
      setSelectedItems([...selectedItems, item]);
    }
    setShowBrowseModal(false);
  };

  const handleRemoveItem = (itemId) => {
    setSelectedItems(selectedItems.filter(i => i.id !== itemId));
  };

  const handleCreateNew = (newItem) => {
    const item = {
      id: Date.now(),
      ...newItem
    };
    setSelectedItems([...selectedItems, item]);
    setShowCreateModal(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateStep2()) return;

    setIsSubmitting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      const projectDataToSave = {
        ...formData,
        [formData.projectType === 'TOWER' ? 'towerSites' : 'segments']: selectedItems,
        [formData.projectType === 'TOWER' ? 'towerCount' : 'segmentCount']: selectedItems.length,
        [formData.projectType === 'TOWER' ? 'segmentCount' : 'towerCount']: 0
      };
      
      if (onSave) {
        onSave(projectDataToSave);
      }
      alert(`Project ${mode === 'add' ? 'created' : 'updated'} successfully!`);
    } catch (error) {
      alert('Error submitting form. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <div className="flex items-center gap-4">
          <button 
            onClick={onBack} 
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div className="flex-1">
            <h1 className="text-2xl font-bold text-gray-800">
              {mode === 'add' ? 'Create New Project' : 'Edit Project'}
            </h1>
            <p className="text-gray-600 mt-1">Step {currentStep} of 2 - Complete project information</p>
          </div>
          <div className="flex items-center gap-2">
            <FileText className="w-6 h-6 text-red-600" />
          </div>
        </div>
      </div>

      {/* Main Form Card */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        {/* Progress Steps */}
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
          <div className="flex items-center justify-center">
            <div className={`flex items-center ${currentStep >= 1 ? 'text-red-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg shadow-md ${
                currentStep >= 1 ? 'bg-gradient-to-br from-red-600 to-red-700 text-white' : 'bg-gray-200'
              }`}>
                1
              </div>
              <span className="ml-3 font-semibold">Project Info</span>
            </div>
            <div className={`mx-6 h-1 w-24 rounded-full ${currentStep >= 2 ? 'bg-red-600' : 'bg-gray-300'}`}></div>
            <div className={`flex items-center ${currentStep >= 2 ? 'text-red-600' : 'text-gray-400'}`}>
              <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-lg shadow-md ${
                currentStep >= 2 ? 'bg-gradient-to-br from-red-600 to-red-700 text-white' : 'bg-gray-200'
              }`}>
                2
              </div>
              <span className="ml-3 font-semibold">Add {formData.projectType === 'TOWER' ? 'Tower Sites' : 'Segments'}</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit}>
          {/* Step 1: Project Info */}
          {currentStep === 1 && (
            <div className="p-6">
              <div className="max-w-3xl mx-auto space-y-6">
                {/* SPK Selection */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Select SPK <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="spkId"
                    value={formData.spkId}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition-all ${
                      errors.spkId ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                  >
                    <option value="">-- Select SPK --</option>
                    {availableSPKs.map(spk => (
                      <option key={spk.id} value={spk.id}>
                        {spk.spkNumber} - {spk.customer} ({spk.projectType})
                      </option>
                    ))}
                  </select>
                  {errors.spkId && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                      <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                      {errors.spkId}
                    </p>
                  )}
                </div>

                {/* Auto-filled fields from SPK */}
                {formData.spkId && (
                  <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                    <div>
                      <label className="block text-xs font-medium text-blue-700 mb-1">Customer</label>
                      <p className="text-sm font-semibold text-gray-900">{formData.customerName}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-blue-700 mb-1">Project Type</label>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        formData.projectType === 'TOWER' 
                          ? 'bg-blue-100 text-blue-800' 
                          : 'bg-green-100 text-green-800'
                      }`}>
                        {formData.projectType}
                      </span>
                    </div>
                  </div>
                )}

                {/* Project Code */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Project Code <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="projectCode"
                    value={formData.projectCode}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition-all ${
                      errors.projectCode ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g., TSEL-JKT-2025-001"
                  />
                  {errors.projectCode && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                      <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                      {errors.projectCode}
                    </p>
                  )}
                </div>

                {/* Project Name */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Project Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="projectName"
                    value={formData.projectName}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition-all ${
                      errors.projectName ? 'border-red-500 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="e.g., Telkomsel Jakarta Tower Project"
                  />
                  {errors.projectName && (
                    <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                      <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                      {errors.projectName}
                    </p>
                  )}
                </div>

                {/* Date Range */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Start Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="startDate"
                      value={formData.startDate}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition-all ${
                        errors.startDate ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                    {errors.startDate && (
                      <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                        <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                        {errors.startDate}
                      </p>
                    )}
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      End Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="endDate"
                      value={formData.endDate}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 transition-all ${
                        errors.endDate ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    />
                    {errors.endDate && (
                      <p className="mt-2 text-sm text-red-600 flex items-center gap-1">
                        <span className="w-1 h-1 bg-red-600 rounded-full"></span>
                        {errors.endDate}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Add Items */}
          {currentStep === 2 && (
            <div className="p-6">
              <div className="max-w-4xl mx-auto space-y-6">
                <div className="flex justify-between items-center">
                  <div>
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                      {formData.projectType === 'TOWER' ? (
                        <>
                          <Building2 className="w-5 h-5 text-blue-600" />
                          Tower Sites
                        </>
                      ) : (
                        <>
                          <Network className="w-5 h-5 text-green-600" />
                          FO Segments
                        </>
                      )}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Add {formData.projectType === 'TOWER' ? 'tower sites' : 'segments'} to this project
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={() => setShowBrowseModal(true)}
                      className="px-4 py-2 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 flex items-center gap-2 font-medium transition-all"
                    >
                      <Search className="w-4 h-4" />
                      Browse Existing
                    </button>
                    <button
                      type="button"
                      onClick={() => setShowCreateModal(true)}
                      className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center gap-2 font-medium shadow-md hover:shadow-lg transition-all"
                    >
                      <Plus className="w-4 h-4" />
                      Create New
                    </button>
                  </div>
                </div>

                {errors.items && (
                  <div className="p-4 bg-red-50 border-l-4 border-red-500 rounded-lg">
                    <p className="text-sm text-red-800 font-medium">{errors.items}</p>
                  </div>
                )}

                <div className="space-y-3">
                  {selectedItems.length === 0 ? (
                    <div className="text-center py-16 border-2 border-dashed border-gray-300 rounded-lg bg-gray-50">
                      {formData.projectType === 'TOWER' ? (
                        <Building2 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      ) : (
                        <Network className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                      )}
                      <p className="text-gray-500 font-medium">No {formData.projectType === 'TOWER' ? 'tower sites' : 'segments'} added yet</p>
                      <p className="text-sm text-gray-400 mt-1">Click "Browse Existing" or "Create New" to add items</p>
                    </div>
                  ) : (
                    selectedItems.map((item, idx) => (
                      <div key={item.id} className="border-2 border-gray-200 rounded-lg p-4 flex items-center justify-between hover:border-blue-500 hover:bg-blue-50 transition-all">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            formData.projectType === 'TOWER' 
                              ? 'bg-blue-100 text-blue-600' 
                              : 'bg-green-100 text-green-600'
                          }`}>
                            {formData.projectType === 'TOWER' ? (
                              <Building2 className="w-5 h-5" />
                            ) : (
                              <Network className="w-5 h-5" />
                            )}
                          </div>
                          <div>
                            {formData.projectType === 'TOWER' ? (
                              <>
                                <div className="font-semibold text-gray-900">{item.siteName}</div>
                                <div className="text-sm text-gray-600 flex items-center gap-2">
                                  <span>{item.location}</span>
                                  <span className="w-1 h-1 bg-gray-400 rounded-full"></span>
                                  <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs rounded-full font-medium">
                                    {item.category}
                                  </span>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="font-semibold text-gray-900">{item.segmentName}</div>
                                <div className="text-sm text-gray-600">
                                  {item.startPoint} → {item.endPoint} 
                                  <span className="ml-2 px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                                    {item.length} km
                                  </span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={() => handleRemoveItem(item.id)}
                          className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                        >
                          <X className="w-5 h-5" />
                        </button>
                      </div>
                    ))
                  )}
                </div>

                {selectedItems.length > 0 && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm text-green-800">
                      <strong>{selectedItems.length}</strong> {formData.projectType === 'TOWER' ? 'tower site(s)' : 'segment(s)'} added to project
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg flex justify-between">
            <div>
              {currentStep > 1 && (
                <button
                  type="button"
                  onClick={handleBack}
                  className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition-colors"
                >
                  Back
                </button>
              )}
            </div>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={onBack}
                className="px-6 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition-colors"
              >
                Cancel
              </button>
              {currentStep === 1 ? (
                <button
                  type="button"
                  onClick={handleNext}
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center gap-2 shadow-md hover:shadow-lg transition-all"
                >
                  Next Step
                  <ChevronRight className="w-5 h-5" />
                </button>
              ) : (
                <button
                  type="submit"
                  className="px-6 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed shadow-md hover:shadow-lg transition-all"
                  disabled={isSubmitting}
                >
                  <Save className="w-5 h-5" />
                  {isSubmitting ? 'Saving...' : (mode === 'add' ? 'Create Project' : 'Update Project')}
                </button>
              )}
            </div>
          </div>
        </form>
      </div>

      {/* Browse Modal */}
      {showBrowseModal && (
        <BrowseModal
          items={formData.projectType === 'TOWER' ? availableTowerSites : availableSegments}
          projectType={formData.projectType}
          onAdd={handleAddItem}
          onClose={() => setShowBrowseModal(false)}
        />
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <CreateModal
          projectType={formData.projectType}
          onCreate={handleCreateNew}
          onClose={() => setShowCreateModal(false)}
        />
      )}
    </div>
  );
};

// Browse Modal Component
const BrowseModal = ({ items, projectType, onAdd, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden">
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-900 to-blue-800 text-white">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold">Browse {projectType === 'TOWER' ? 'Tower Sites' : 'Segments'}</h3>
              <p className="text-blue-200 text-sm mt-1">Select items to add to your project</p>
            </div>
            <button onClick={onClose} className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        <div className="p-6 overflow-y-auto max-h-[calc(85vh-140px)]">
          <div className="space-y-3">
            {items.filter(item => !item.isAttached).length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-500">No available {projectType === 'TOWER' ? 'tower sites' : 'segments'} found</p>
              </div>
            ) : (
              items.filter(item => !item.isAttached).map(item => (
                <div key={item.id} className="border-2 border-gray-200 rounded-lg p-4 flex items-center justify-between hover:border-blue-500 hover:bg-blue-50 transition-all">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                      projectType === 'TOWER' 
                        ? 'bg-blue-100 text-blue-600' 
                        : 'bg-green-100 text-green-600'
                    }`}>
                      {projectType === 'TOWER' ? (
                        <Building2 className="w-5 h-5" />
                      ) : (
                        <Network className="w-5 h-5" />
                      )}
                    </div>
                    <div>
                      {projectType === 'TOWER' ? (
                        <>
                          <div className="font-semibold text-gray-900">{item.siteName}</div>
                          <div className="text-sm text-gray-600">{item.location} - {item.category}</div>
                        </>
                      ) : (
                        <>
                          <div className="font-semibold text-gray-900">{item.segmentName}</div>
                          <div className="text-sm text-gray-600">{item.startPoint} → {item.endPoint} ({item.length} km)</div>
                        </>
                      )}
                    </div>
                  </div>
                  <button
                    onClick={() => onAdd(item)}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors shadow-md hover:shadow-lg"
                  >
                    Add
                  </button>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Create Modal Component
const CreateModal = ({ projectType, onCreate, onClose }) => {
  const [newItem, setNewItem] = useState(
    projectType === 'TOWER'
      ? { siteName: '', location: '', category: 'RING' }
      : { segmentName: '', startPoint: '', endPoint: '', length: '' }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreate(newItem);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-md w-full">
        <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-red-600 to-red-700 text-white">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-xl font-bold">Create New {projectType === 'TOWER' ? 'Tower Site' : 'Segment'}</h3>
              <p className="text-red-100 text-sm mt-1">Enter details below</p>
            </div>
            <button onClick={onClose} className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-2 transition-colors">
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>
        <form onSubmit={handleSubmit} className="p-6">
          <div className="space-y-4">
            {projectType === 'TOWER' ? (
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Site Name</label>
                  <input
                    type="text"
                    value={newItem.siteName}
                    onChange={(e) => setNewItem({ ...newItem, siteName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Location</label>
                  <input
                    type="text"
                    value={newItem.location}
                    onChange={(e) => setNewItem({ ...newItem, location: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                  <select
                    value={newItem.category}
                    onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                  >
                    <option value="RING">RING</option>
                    <option value="STAR">STAR</option>
                    <option value="INSERT">INSERT</option>
                  </select>
                </div>
              </>
            ) : (
              <>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Segment Name</label>
                  <input
                    type="text"
                    value={newItem.segmentName}
                    onChange={(e) => setNewItem({ ...newItem, segmentName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Start Point</label>
                  <input
                    type="text"
                    value={newItem.startPoint}
                    onChange={(e) => setNewItem({ ...newItem, startPoint: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">End Point</label>
                  <input
                    type="text"
                    value={newItem.endPoint}
                    onChange={(e) => setNewItem({ ...newItem, endPoint: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">Length (km)</label>
                  <input
                    type="number"
                    value={newItem.length}
                    onChange={(e) => setNewItem({ ...newItem, length: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500"
                    required
                  />
                </div>
              </>
            )}
          </div>
          <div className="mt-6 flex justify-end gap-3">
            <button 
              type="button" 
              onClick={onClose} 
              className="px-5 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 font-medium transition-colors"
            >
              Cancel
            </button>
            <button 
              type="submit" 
              className="px-5 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium shadow-md hover:shadow-lg transition-all"
            >
              Create
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectMilestoneForm;